import {
  Checkbox,
  Col,
  DatePicker,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
} from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaUpload } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { error, success } from "../../contants/snackbars";
import { bannerTypes, colorScheme, pageTypes } from "../../contants/variables";
import { addBanner, editBanner } from "../../stores/api_calls/bannerlists";
import { uploadImage, uploadVideo } from "../../stores/api_calls/upload";
import { validateBannerForm } from "../../stores/helpers/bannerFormValidation";
import "./banner.scss";

const RESPONSIVE_TABLET_WIDTH = 1024;
const RESPONSIVE_MOBILE_WIDTH = 400;

const createEmptyFileState = () => ({ file: null, url: "" });

const createInitialForm = () => ({
  bannerId: "",
  bannerName: "",
  adType: "photo",
  bannerType: "",
  pageType: "",
  url: "https://vincarmart.com",
  is_external_link: false,
  clientName: "",
  startDate: moment.utc().local().format("YYYY-MM-DD"),
  endDate: moment.utc().local().format("YYYY-MM-DD"),
  imageUrl: "",
  tabletImageUrl: "",
  mobileImageUrl: "",
  imageId: "",
  tabletImageId: "",
  mobileImageId: "",
  adsLocation: "",
  timer: null,
  bannerVideo: "",
  tabletBannerVideo: "",
  mobileBannerVideo: "",
  videoType: "youtube",
  localVideoUrl: "",
  tabletLocalVideoUrl: "",
  mobileLocalVideoUrl: "",
  localVideoId: "",
  tabletLocalVideoId: "",
  mobileLocalVideoId: "",
});

const DESKTOP_DIMENSIONS = {
  "Hero-Banner": { height: 350, width: 1520 },
  "Strip-Banner": { height: 100, width: 1200 },
  "Popout-Long": { height: 100, width: 1520 },
  "Popout-Rectangle": { height: 400, width: 600 },
  "Vertical-Banner": { height: 540, width: 300 },
  "Auth-Left-Side": { height: 540, width: 300 },
  "Auth-Right-Side": { height: 540, width: 300 },
  "Product-View-Center-Ad": { height: 300, width: 862 },
  "Plate-Numbers-Fade-Slide-Ad": { height: 280, width: 393 },
  "Car-Rental-Fade-Slide-Ad": { height: 280, width: 393 },
  "Commercial-Vehicle-Fade-Slide-Ad": { height: 280, width: 393 },
  "Desktop-Product-Banner": { height: 200, width: 960 },
  "rental-strip-banner-ad": { height: 200, width: 960 },
  "Plate-Numbers-Strip-Banner": { height: 200, width: 980 },
  "Home-Strip-Banner-1": { height: 200, width: 980 },
  "Home-Strip-Banner-2": { height: 200, width: 980 },
  "Home-Strip-Banner-3": { height: 200, width: 980 },
  "Home-Strip-Banner-4": { height: 200, width: 980 },
  "Home-Strip-Banner-5": { height: 200, width: 980 },
  "Vertical-Side-Banner": { height: 600, width: 300 },
  "Strip-Banner-I-Max": { height: 360, width: 700 },
  "valuation-Rectangle-strip-banner-ad": { height: 360, width: 700 },
  "I-Max": { height: 250, width: 700 },
  "Mobile-Ad": { height: 200, width: 500 },
  "Mobile-Strip-Banner-Ad": { height: 200, width: 400 },
  "Home-Search-Small-Banner": { height: 130, width: 200 },
  "used-car-main-banner-ad": { height: 350, width: 1400 },
  "new-car-main-banner-ad": { height: 350, width: 1400 },
  "rental-car-main-banner-ad": { height: 350, width: 1400 },
  "car-bid-main-banner-ad": { height: 350, width: 1400 },
  "video-main-banner-ad": { height: 350, width: 1400 },
  "article-main-banner-ad": { height: 350, width: 1400 },
  "insurance-main-banner-ad": { height: 350, width: 1400 },
  "workshop-main-banner-ad": { height: 350, width: 1400 },
  "loan-calculator-main-banner-ad": { height: 350, width: 1400 },
  "direct-seller-main-banner-ad": { height: 350, width: 1400 },
  "hybrid-car-main-banner-ad": { height: 400, width: 1920 },
  "electric-car-main-banner-ad": { height: 400, width: 1920 },
};

const formatSizing = (height, width) =>
  `${height} height x ${width} width pixels`;

const getDesktopDimensions = (type) =>
  DESKTOP_DIMENSIONS[type] || { height: 300, width: 300 };

const getScaledSizing = (type, multiplier) => {
  const { height, width } = getDesktopDimensions(type);
  return formatSizing(height * multiplier, width * multiplier);
};

const getResponsiveSizing = (type, targetWidth, multiplier) => {
  const { height, width } = getDesktopDimensions(type);

  if (!width || !height) {
    return "";
  }

  const scaledWidth = Math.round(targetWidth * multiplier);
  const scaledHeight = Math.round((height / width) * scaledWidth);

  return formatSizing(scaledHeight, scaledWidth);
};

const buildEditForm = (data) => ({
  ...createInitialForm(),
  bannerId: data.id,
  bannerName: data.name,
  adType: data.ad_type || "photo",
  bannerType: data.banner_type,
  pageType: data.page_type,
  url: data.link || "https://vincarmart.com",
  is_external_link: data.is_external_link || false,
  clientName: data.client_name,
  startDate: moment.utc(data.start_date).local().format("YYYY-MM-DD"),
  endDate: moment.utc(data.end_date).local().format("YYYY-MM-DD"),
  imageId: data.banner_image_urls ? data.banner_image_urls._id["$oid"] : null,
  tabletImageId: data.tablet_banner_image_urls
    ? data.tablet_banner_image_urls._id["$oid"]
    : null,
  mobileImageId: data.mobile_banner_image_urls
    ? data.mobile_banner_image_urls._id["$oid"]
    : null,
  imageUrl: data.banner_image_urls
    ? data.banner_image_urls.metadata.image_url
    : data.desktop_banner_image_url || null,
  tabletImageUrl: data.tablet_banner_image_url || null,
  mobileImageUrl: data.mobile_banner_image_url || null,
  bannerVideo: data.banner_video || "",
  tabletBannerVideo: data.tablet_banner_video || "",
  mobileBannerVideo: data.mobile_banner_video || "",
  videoType: data.video_type || "youtube",
  localVideoUrl: data.local_video_url || "",
  tabletLocalVideoUrl: data.tablet_local_video_url || "",
  mobileLocalVideoUrl: data.mobile_local_video_url || "",
  localVideoId: data.banner_video_urls ? data.banner_video_urls._id["$oid"] : null,
  tabletLocalVideoId: data.tablet_banner_video_urls
    ? data.tablet_banner_video_urls._id["$oid"]
    : null,
  mobileLocalVideoId: data.mobile_banner_video_urls
    ? data.mobile_banner_video_urls._id["$oid"]
    : null,
});

const AdForm = (props) => {
  const { isEdit, data, onCancel, visible, fetchData, tablePagination } = props;
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form, setForm] = useState(createInitialForm);
  const [selectedFile, setSelectedFile] = useState(createEmptyFileState);
  const [selectedFileTablet, setSelectedFileTablet] = useState(createEmptyFileState);
  const [selectedFileMobile, setSelectedFileMobile] = useState(createEmptyFileState);
  const [selectedVideo, setSelectedVideo] = useState(createEmptyFileState);
  const [selectedVideoTablet, setSelectedVideoTablet] = useState(createEmptyFileState);
  const [selectedVideoMobile, setSelectedVideoMobile] = useState(createEmptyFileState);

  const fileUploader = useRef(null);
  const fileUploaderTablet = useRef(null);
  const fileUploaderMobile = useRef(null);
  const fileUploaderVideo = useRef(null);
  const fileUploaderVideoTablet = useRef(null);
  const fileUploaderVideoMobile = useRef(null);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setSelectedFile(createEmptyFileState());
    setSelectedFileTablet(createEmptyFileState());
    setSelectedFileMobile(createEmptyFileState());
    setSelectedVideo(createEmptyFileState());
    setSelectedVideoTablet(createEmptyFileState());
    setSelectedVideoMobile(createEmptyFileState());

    if (isEdit) {
      setForm(buildEditForm(data));
      return;
    }

    setForm(createInitialForm());
  }, [data, isEdit, visible]);

  const refetchLists = () => {
    const pagination = {
      page: tablePagination.current,
      limit: tablePagination.pageSize,
      keyword: "",
    };

    fetchData(pagination);
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onCheckboxChange = (name, e) => {
    updateForm(name, e.target.checked);
  };

  const handleFieldChange = (name, e) => {
    updateForm(name, e.target.value);
  };

  const handleDropdownChange = (name, value) => {
    updateForm(name, value);
  };

  const onDateFieldChange = (name, value) => {
    updateForm(name, value);
  };

  const handleOptionChange = (name, e) => {
    const value = e.target.value;

    setForm((prev) => {
      const nextForm = { ...prev, [name]: value };

      if (name === "adType" && value === "photo") {
        nextForm.videoType = "youtube";
      }

      return nextForm;
    });
  };

  const handleOpenGallery = (ref) => {
    ref.current.click();
  };

  const handleImageChange = (e, setter, formKey, idKey) => {
    setter(createEmptyFileState());

    if (!e.target.files?.length) {
      return;
    }

    const file = e.target.files[0];
    const url = URL.createObjectURL(file);

    setter({ file, url });
    setForm((prev) => ({
      ...prev,
      [formKey]: url,
      [idKey]: "",
    }));
  };

  const handleVideoChange = (e, setter, urlKey, idKey, youtubeKey) => {
    setter(createEmptyFileState());

    if (!e.target.files?.length) {
      return;
    }

    const file = e.target.files[0];
    const url = URL.createObjectURL(file);

    setter({ file, url });
    setForm((prev) => ({
      ...prev,
      [urlKey]: url,
      [idKey]: "",
      [youtubeKey]: "",
    }));
  };

  const handleClearLocalVideo = (urlKey, idKey, setter) => {
    setter(createEmptyFileState());
    setForm((prev) => ({
      ...prev,
      [urlKey]: "",
      [idKey]: "",
    }));
  };

  const resetFormState = () => {
    setSubmitLoading(false);
    setSelectedFile(createEmptyFileState());
    setSelectedFileTablet(createEmptyFileState());
    setSelectedFileMobile(createEmptyFileState());
    setSelectedVideo(createEmptyFileState());
    setSelectedVideoTablet(createEmptyFileState());
    setSelectedVideoMobile(createEmptyFileState());
    setForm(createInitialForm());
  };

  const onClose = () => {
    resetFormState();
    onCancel();
  };

  const uploadMedia = async (draft) => {
    const uploadImageField = async (fileState, urlKey, idKey) => {
      if (!fileState.file) {
        return;
      }

      const res = await uploadImage(fileState.file);

      if (res.data.success) {
        draft[urlKey] = res?.data?.data?.metadata?.image_url || null;
        draft[idKey] = res?.data?.data?._id || null;
      }
    };

    const uploadVideoField = async (fileState, urlKey, idKey) => {
      if (!fileState.file) {
        return;
      }

      const res = await uploadVideo(fileState.file);

      if (res.data.success) {
        draft[urlKey] = res?.data?.data?.metadata?.file_url || null;
        draft[idKey] = res?.data?.data?._id || null;
      }
    };

    await uploadImageField(selectedFile, "imageUrl", "imageId");
    await uploadImageField(
      selectedFileTablet,
      "tabletImageUrl",
      "tabletImageId",
    );
    await uploadImageField(
      selectedFileMobile,
      "mobileImageUrl",
      "mobileImageId",
    );
    await uploadVideoField(selectedVideo, "localVideoUrl", "localVideoId");
    await uploadVideoField(
      selectedVideoTablet,
      "tabletLocalVideoUrl",
      "tabletLocalVideoId",
    );
    await uploadVideoField(
      selectedVideoMobile,
      "mobileLocalVideoUrl",
      "mobileLocalVideoId",
    );
  };

  const submitBanner = async (isUpdateAction) => {
    setSubmitLoading(true);

    const draft = { ...form };

    try {
      await uploadMedia(draft);

      const request = isUpdateAction ? editBanner(draft) : addBanner(draft);
      const res = await request;

      if (res?.data?.success) {
        refetchLists();
        success(
          isUpdateAction
            ? "Banner Updated Successfully"
            : "New Banner Added Successfully",
        );
        onClose();
      } else {
        setSubmitLoading(false);
      }
    } catch (e) {
      setSubmitLoading(false);
      error(
        `Opps! ${e?.response?.data?.server_response || "Request failed"} . Fetched Failed. Please try again later`,
      );
    }
  };

  const generateSizing = (type, multiplier) => getScaledSizing(type, multiplier);

  const isResponsive = useMemo(() => {
    const selectedBannerType = bannerTypes.find(
      (item) => item.value == form.bannerType,
    );

    return selectedBannerType?.responsive || false;
  }, [form.bannerType]);

  const generateTabletSizing = useCallback(
    (type, multiplier) => {
      if (!isResponsive) {
        return "";
      }

      return getResponsiveSizing(type, RESPONSIVE_TABLET_WIDTH, multiplier);
    },
    [isResponsive],
  );

  const generateMobileSizing = useCallback(
    (type, multiplier) => {
      if (!isResponsive) {
        return "";
      }

      return getResponsiveSizing(type, RESPONSIVE_MOBILE_WIDTH, multiplier);
    },
    [isResponsive],
  );

  const renderLocalVideoPreview = (src, onClear) => {
    if (!src) {
      return null;
    }

    return (
      <div className="form-container video-container">
        <div
          className="image-preview-container"
          style={{ display: "flex", justifyContent: "end" }}
        >
          <video
            src={src}
            controls
            width="150"
            style={{
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
          <IoIosCloseCircleOutline
            onClick={onClear}
            style={{
              color: "red",
              fontSize: 20,
              cursor: "pointer",
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      <Modal
        className="formModal"
        title={`${isEdit ? "Edit" : "Create"} Ad Banner`}
        visible={visible}
        width={800}
        onOk={() => submitBanner(isEdit)}
        okButtonProps={{ disabled: !validateBannerForm(form) }}
        onCancel={onClose}
        okText={`${isEdit ? "Save" : "Submit"}`}
        confirmLoading={submitLoading}
      >
        <Row gutter={[20, 20]}>
          <Col span={24}>
            <div className="form-container">
              <p className="form-label">
                Banner Name <span>*</span> :{" "}
              </p>
              <Input
                value={form.bannerName}
                onChange={(text) => handleFieldChange("bannerName", text)}
                placeholder="Please type Banner Name"
              />
            </div>

            <div className="form-container">
              <p className="form-label">
                Link Url <span>*</span> :{" "}
              </p>
              <Input
                value={form.url}
                onChange={(text) => handleFieldChange("url", text)}
                placeholder="Please type link url"
              />
            </div>

            <div className="form-container">
              <p className="form-label"></p>
              <div style={{ width: "100%" }}>
                <Checkbox
                  valuePropName="checked"
                  checked={form.is_external_link}
                  onChange={(e) => onCheckboxChange("is_external_link", e)}
                >
                  Is external link
                </Checkbox>
              </div>
            </div>

            <div className="form-container">
              <p className="form-label">
                Client Name <span>*</span> :{" "}
              </p>
              <Input
                value={form.clientName}
                onChange={(text) => handleFieldChange("clientName", text)}
                placeholder="Please type Client Name"
              />
            </div>

            <div className="form-container">
              <p className="form-label">
                Start Date <span>*</span> :{" "}
              </p>
              <Space>
                <div className="datepicker">
                  <DatePicker
                    style={{ width: "100%" }}
                    value={moment(form.startDate)}
                    onChange={(date, dateString) =>
                      onDateFieldChange("startDate", dateString)
                    }
                  />
                </div>
              </Space>
            </div>

            {visible ? (
              <div className="form-container">
                <p className="form-label">
                  End Date <span>*</span> :{" "}
                </p>
                <Space>
                  <div className="datepicker">
                    <DatePicker
                      style={{ width: "100%" }}
                      value={moment(form.endDate)}
                      onChange={(date, dateString) =>
                        onDateFieldChange("endDate", dateString)
                      }
                    />
                  </div>
                </Space>
              </div>
            ) : null}

            <div className="form-container">
              <p className="form-label">
                Ad Type <span>*</span> :{" "}
              </p>
              <div style={{ width: "100%" }}>
                <Radio.Group
                  onChange={(value) => handleOptionChange("adType", value)}
                  value={form.adType || undefined}
                >
                  <Radio value="photo">Photo</Radio>
                  <Radio value="video">Video</Radio>
                </Radio.Group>
              </div>
            </div>
          </Col>

          <Col span={24}>
            {form.adType === "photo" ? (
              <div>
                <p style={{ margin: "5px 0x", color: colorScheme.red }}>
                  {`To avoid stretching of image in the website. The image should not exceed 2MB(megabyte) and has to be (.jpg , .png) with a minimum size and same ratio.`}
                </p>
              </div>
            ) : null}

            <div className="form-container">
              <p className="form-label">
                Page Type <span>*</span> :{" "}
              </p>
              <Select
                showSearch
                value={form.pageType || undefined}
                onChange={(value) => handleDropdownChange("pageType", value)}
                placeholder="Search to Select"
                style={{ width: "100%" }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {pageTypes.map((page, i) => (
                  <Select.Option key={i} value={page.value}>
                    {page.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="form-container">
              <p className="form-label">
                Banner Type <span>*</span> :{" "}
              </p>
              <Select
                showSearch
                value={form.bannerType || undefined}
                onChange={(value) => handleDropdownChange("bannerType", value)}
                placeholder="Search to Select"
                style={{ width: "100%" }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                filterSort={(optionA, optionB) =>
                  optionA.children
                    .toLowerCase()
                    .localeCompare(optionB.children.toLowerCase())
                }
              >
                {bannerTypes.map((banner, i) => (
                  <Select.Option key={i} value={banner.value}>
                    {banner.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {form.adType === "photo" ? (
              <>
                <div className="form-container label-container">
                  <p className="form-label"></p>
                  <p style={{ width: "100%" }}>
                    <span
                      style={{ color: colorScheme.green, fontSize: "0.9em" }}
                    >
                      {generateSizing(form.bannerType, 1)}
                    </span>
                  </p>
                </div>

                <div className="form-container photo-container">
                  <p className="form-label">
                    Banner Image <span>*</span> :{" "}
                  </p>
                  <div
                    className="upload-container"
                    style={{ width: "100%" }}
                    onClick={() => handleOpenGallery(fileUploader)}
                  >
                    <FaUpload />
                    <p className="banner-p">UPLOAD BANNER PHOTO</p>
                  </div>

                  <input
                    ref={fileUploader}
                    id="getFile"
                    type="file"
                    hidden
                    onChange={(e) =>
                      handleImageChange(
                        e,
                        setSelectedFile,
                        "imageUrl",
                        "imageId",
                      )
                    }
                  />
                </div>

                {form.imageUrl && (
                  <div className="image-preview-container">
                    <img src={form.imageUrl} />
                  </div>
                )}

                {isResponsive && (
                  <>
                    <div className="form-container label-container">
                      <p className="form-label"></p>
                      <p style={{ width: "100%" }}>
                        <span
                          style={{
                            color: colorScheme.green,
                            fontSize: "0.9em",
                          }}
                        >
                          {generateTabletSizing(form.bannerType, 1)}
                        </span>
                      </p>
                    </div>

                    <div className="form-container photo-container">
                      <p className="form-label">Tablet Banner Image : </p>
                      <div
                        className="upload-container"
                        style={{ width: "100%" }}
                        onClick={() => handleOpenGallery(fileUploaderTablet)}
                      >
                        <FaUpload />
                        <p className="banner-p">UPLOAD TABLET BANNER PHOTO</p>
                      </div>

                      <input
                        ref={fileUploaderTablet}
                        id="getFileTablet"
                        type="file"
                        hidden
                        onChange={(e) =>
                          handleImageChange(
                            e,
                            setSelectedFileTablet,
                            "tabletImageUrl",
                            "tabletImageId",
                          )
                        }
                      />
                    </div>

                    {form.tabletImageUrl && (
                      <div className="image-preview-container">
                        <img src={form.tabletImageUrl} />
                      </div>
                    )}

                    <div className="form-container label-container">
                      <p className="form-label"></p>
                      <p style={{ width: "100%" }}>
                        <span
                          style={{
                            color: colorScheme.green,
                            fontSize: "0.9em",
                          }}
                        >
                          {generateMobileSizing(form.bannerType, 1)}
                        </span>
                      </p>
                    </div>

                    <div className="form-container photo-container">
                      <p className="form-label">Mobile Banner Image : </p>
                      <div
                        className="upload-container"
                        style={{ width: "100%" }}
                        onClick={() => handleOpenGallery(fileUploaderMobile)}
                      >
                        <FaUpload />
                        <p className="banner-p">UPLOAD MOBILE BANNER PHOTO</p>
                      </div>
                      <input
                        ref={fileUploaderMobile}
                        id="getFileMobile"
                        type="file"
                        hidden
                        onChange={(e) =>
                          handleImageChange(
                            e,
                            setSelectedFileMobile,
                            "mobileImageUrl",
                            "mobileImageId",
                          )
                        }
                      />
                    </div>

                    {form.mobileImageUrl && (
                      <div className="image-preview-container">
                        <img src={form.mobileImageUrl} />
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <div className="form-container">
                  <p className="form-label">Video Type : </p>
                  <div style={{ width: "100%" }}>
                    <Radio.Group
                      onChange={(value) =>
                        handleOptionChange("videoType", value)
                      }
                      value={form.videoType || undefined}
                    >
                      <Radio value="youtube">Youtube</Radio>
                      <Radio value="local">Local</Radio>
                    </Radio.Group>
                  </div>
                </div>

                {form.videoType === "local" && (
                  <div
                    className="form-container"
                    style={{ justifyContent: "end" }}
                  >
                    <span style={{ color: "red" }}>
                      Only MP4 video allowed.
                    </span>
                  </div>
                )}

                <div className="form-container label-container">
                  <p className="form-label"></p>
                  <p style={{ width: "100%" }}>
                    <span
                      style={{ color: colorScheme.green, fontSize: "0.9em" }}
                    >
                      {generateSizing(form.bannerType, 1)}
                    </span>
                  </p>
                </div>

                <div className="form-container video-container">
                  <p className="form-label">
                    Banner Video <span>*</span> :{" "}
                  </p>
                  {form.videoType === "youtube" ? (
                    <Input
                      value={form.bannerVideo}
                      onChange={(text) =>
                        handleFieldChange("bannerVideo", text)
                      }
                      placeholder="Please input Banner Video"
                    />
                  ) : (
                    <>
                      <div
                        className="upload-container"
                        style={{ width: "100%" }}
                        onClick={() => handleOpenGallery(fileUploaderVideo)}
                      >
                        <FaUpload />
                        <p className="banner-p">UPLOAD VIDEO</p>
                      </div>

                      <input
                        ref={fileUploaderVideo}
                        id="getFileVideo"
                        type="file"
                        hidden
                        onChange={(e) =>
                          handleVideoChange(
                            e,
                            setSelectedVideo,
                            "localVideoUrl",
                            "localVideoId",
                            "bannerVideo",
                          )
                        }
                        accept=".mp4"
                      />
                    </>
                  )}
                </div>

                {form.videoType === "local" &&
                  renderLocalVideoPreview(form.localVideoUrl, () =>
                    handleClearLocalVideo(
                      "localVideoUrl",
                      "localVideoId",
                      setSelectedVideo,
                    ),
                  )}

                {isResponsive && (
                  <>
                    <div className="form-container label-container">
                      <p className="form-label"></p>
                      <p style={{ width: "100%" }}>
                        <span
                          style={{
                            color: colorScheme.green,
                            fontSize: "0.9em",
                          }}
                        >
                          {generateTabletSizing(form.bannerType, 1)}
                        </span>
                      </p>
                    </div>

                    <div className="form-container video-container">
                      <p className="form-label">Tablet Banner Video : </p>
                      {form.videoType === "youtube" ? (
                        <Input
                          value={form.tabletBannerVideo}
                          onChange={(text) =>
                            handleFieldChange("tabletBannerVideo", text)
                          }
                          placeholder="Please input Tablet Banner Video"
                        />
                      ) : (
                        <>
                          <div
                            className="upload-container"
                            style={{ width: "100%" }}
                            onClick={() =>
                              handleOpenGallery(fileUploaderVideoTablet)
                            }
                          >
                            <FaUpload />
                            <p className="banner-p">UPLOAD TABLET VIDEO</p>
                          </div>

                          <input
                            ref={fileUploaderVideoTablet}
                            id="getFileVideoTablet"
                            type="file"
                            hidden
                            onChange={(e) =>
                              handleVideoChange(
                                e,
                                setSelectedVideoTablet,
                                "tabletLocalVideoUrl",
                                "tabletLocalVideoId",
                                "tabletBannerVideo",
                              )
                            }
                            accept=".mp4"
                          />
                        </>
                      )}
                    </div>

                    {form.videoType === "local" &&
                      renderLocalVideoPreview(form.tabletLocalVideoUrl, () =>
                        handleClearLocalVideo(
                          "tabletLocalVideoUrl",
                          "tabletLocalVideoId",
                          setSelectedVideoTablet,
                        ),
                      )}

                    <div className="form-container label-container">
                      <p className="form-label"></p>
                      <p style={{ width: "100%" }}>
                        <span
                          style={{
                            color: colorScheme.green,
                            fontSize: "0.9em",
                          }}
                        >
                          {generateMobileSizing(form.bannerType, 1)}
                        </span>
                      </p>
                    </div>

                    <div className="form-container video-container">
                      <p className="form-label">Mobile Banner Video : </p>
                      {form.videoType === "youtube" ? (
                        <Input
                          value={form.mobileBannerVideo}
                          onChange={(text) =>
                            handleFieldChange("mobileBannerVideo", text)
                          }
                          placeholder="Please input Mobile Banner Video"
                        />
                      ) : (
                        <>
                          <div
                            className="upload-container"
                            style={{ width: "100%" }}
                            onClick={() =>
                              handleOpenGallery(fileUploaderVideoMobile)
                            }
                          >
                            <FaUpload />
                            <p className="banner-p">UPLOAD MOBILE VIDEO</p>
                          </div>
                          <input
                            ref={fileUploaderVideoMobile}
                            id="getFileVideoMobile"
                            type="file"
                            hidden
                            onChange={(e) =>
                              handleVideoChange(
                                e,
                                setSelectedVideoMobile,
                                "mobileLocalVideoUrl",
                                "mobileLocalVideoId",
                                "mobileBannerVideo",
                              )
                            }
                            accept=".mp4"
                          />
                        </>
                      )}
                    </div>

                    {form.videoType === "local" &&
                      renderLocalVideoPreview(form.mobileLocalVideoUrl, () =>
                        handleClearLocalVideo(
                          "mobileLocalVideoUrl",
                          "mobileLocalVideoId",
                          setSelectedVideoMobile,
                        ),
                      )}
                  </>
                )}
              </>
            )}
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default AdForm;
